import { lazy, Suspense } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useWebGLSupport } from '@/hooks/useWebGLSupport';
import { useDeviceClass } from '@/hooks/useDeviceClass';
import { HeroPoster } from './HeroPoster';
import { HeroReducedMotion } from './HeroReducedMotion';

const HeroPin = lazy(() =>
  import('./HeroPin').then((m) => ({ default: m.HeroPin }))
);

/**
 * The single decision point for the hero branch. Reads three capability
 * signals on mount and renders exactly one sub-branch.
 *
 * - cinematic: live R3F Monolith + image-sequence fly-through
 * - reduced:   3 keyframe stills with IO crossfades
 * - poster:    single composed still
 */
export function Hero() {
  const reducedMotion = useReducedMotion();
  const webglSupport = useWebGLSupport();
  const deviceClass = useDeviceClass();

  // Until detection completes (first paint), show the safe poster. This is
  // SEO-equivalent to the other branches (same h1, same copy, same CTA).
  if (webglSupport === null || deviceClass === null) {
    return <HeroPoster />;
  }

  if (reducedMotion) {
    return <HeroReducedMotion />;
  }
  if (!webglSupport || deviceClass === 'low') {
    return <HeroPoster />;
  }

  return (
    <Suspense fallback={<HeroPoster />}>
      <HeroPin />
    </Suspense>
  );
}
